import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';

type JsonSchemaEditorModalProps = {
  open: boolean;
  initialValue: string;
  onOpenChange: (open: boolean) => void;
  onSave: (value: string | null) => void;
};

function getValidationError(value: string) {
  if (!value.trim()) {
    return 'Please provide a valid JSON schema with at least one field.';
  }

  try {
    const parsed = JSON.parse(value);

    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Object.keys(parsed).length > 0
    ) {
      return '';
    }

    return 'Please provide a valid JSON schema with at least one field.';
  } catch (error) {
    return error instanceof Error ? error.message : 'Invalid JSON schema.';
  }
}

export function JsonSchemaEditorModal({
  open,
  initialValue,
  onOpenChange,
  onSave,
}: JsonSchemaEditorModalProps) {
  const [jsonValue, setJsonValue] = useState(initialValue);

  useEffect(() => {
    if (open) {
      setJsonValue(initialValue);
    }
  }, [initialValue, open]);

  const validationError = useMemo(() => getValidationError(jsonValue), [jsonValue]);
  const isValid = !validationError;

  const handleSave = () => {
    if (!isValid) {
      return;
    }

    onSave(jsonValue);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>JSON Response Schema</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 overflow-hidden">
          <div className="overflow-hidden rounded-lg border border-border bg-background">
            <Textarea
              value={jsonValue}
              onChange={(event) => setJsonValue(event.target.value)}
              className="min-h-[360px] resize-none border-0 p-4 font-mono text-sm leading-6 shadow-none focus-visible:ring-0"
              spellCheck={false}
            />
          </div>

          {validationError ? (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{validationError}</p>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <p>Valid JSON schema</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!isValid}>
            Save Schema
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
